import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import { 
  Home, 
  BarChart3, 
  Users, 
  Trophy, 
  Menu,
  Zap
} from 'lucide-react';

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<any>;
  badge?: number;
}

const navigation: NavigationItem[] = [
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Draft Analysis', href: '/analysis', icon: BarChart3 },
  { name: 'Leagues', href: '/leagues', icon: Users },
  { name: 'Scenarios', href: '/scenarios', icon: Zap },
  { name: 'Leaderboard', href: '/leaderboard', icon: Trophy },
];

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  const isCurrentPage = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  // Clean layout for dashboard and league detail pages
  const isCleanLayoutPage = location.pathname === '/' || location.pathname.includes('/leagues/');

  if (isCleanLayoutPage) {
    // Clean layout - no sidebar, no header
    return (
      <div className="min-h-screen bg-background-primary">
        <main className="min-h-screen">
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-primary">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Sidebar */}
      <div className={clsx(
        'fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        <div className="flex h-full flex-col bg-background-secondary border-r border-white/5">
          {/* Logo */}
          <div className="flex h-16 items-center px-6 border-b border-white/5">
            <div className="flex items-center">
              <img 
                src="/draftedge_logo.png" 
                alt="DraftEdge"
                className="h-8 w-auto object-contain"
              />
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              const current = isCurrentPage(item.href);
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={clsx(
                    'group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200',
                    current
                      ? 'bg-accent-primary/10 text-accent-primary'
                      : 'text-text-secondary hover:text-text-primary hover:bg-white/5'
                  )}
                >
                  <Icon className={clsx(
                    'mr-3 h-5 w-5 flex-shrink-0 transition-colors',
                    current ? 'text-accent-primary' : 'text-text-tertiary group-hover:text-text-secondary'
                  )} />
                  {item.name}
                  {item.badge && (
                    <span className="ml-auto inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-background-primary bg-accent-primary rounded-full">
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User menu */}
          <div className="flex-shrink-0 p-4 border-t border-white/5">
            <div className="flex items-center">
              <div className="w-8 h-8 rounded-full bg-gradient-secondary flex items-center justify-center">
                <span className="text-sm font-semibold text-white">⚡</span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-text-primary">Demo User</p>
                <p className="text-xs text-text-tertiary">Fantasy Draft Analyst</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col min-h-screen">
        {/* Mobile header */}
        <div className="sticky top-0 z-30 flex h-16 items-center gap-x-4 border-b border-white/5 bg-background-secondary/95 backdrop-blur px-4 sm:px-6 lg:hidden">
          <button
            type="button"
            className="text-text-secondary hover:text-text-primary"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
          
          <div className="flex-1 text-sm font-semibold leading-6 text-text-primary">
            <img 
              src="/draftedge_logo_small.png" 
              alt="DraftEdge"
              className="h-6 w-auto object-contain"
            />
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <div className="px-4 sm:px-6 lg:px-8 py-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout; 